import React from 'react';
import {Typography} from '../typography/Typography';
import {Eyebrow, EyebrowProps} from '../eyebrow/Eyebrow';
import {Image, ImageProps} from '../images/Image';
import {Button} from '../buttons/Button';
import {TextLink} from '../links/TextLink';
import Grid from '@material-ui/core/Grid';
import {VerticalSpacer} from '../spacer/VerticalSpacer';
import {AccentColorOptions, useBreakpointDown, useBreakpointUp} from 'epic-ue-theme';
import {DividedListVariantOptions} from './DividedList';
import {AnimateOnViewWrap} from '../../wraps/animation';
import styled from 'styled-components';
import {UeLink, WithCaption, WithCaptionProps} from '../../wraps';

export const DividedListItemWrapper = styled.div`
    .divided-list-offset {
        height: 100%;
        ${(p) => p.theme.breakpoints.down('sm')} {
            margin-top: 8px;
            margin-bottom: -24px;
        }
    }
    h4,
    .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
    }
    .ue-link-wrap {
        img {
            transition: transform 0.2s ${(p) => p.theme.easing};
            &:hover {
                transform: scale3d(1.01, 1.01, 1.01);
            }
        }
    }
`;

export interface DividedListItemProps {
    accentColor?: AccentColorOptions;
    bottomEyebrow?: EyebrowProps;
    callToAction?: string;
    className?: string;
    description?: string;
    headline?: string;
    title?: string;
    href?: string;
    image?: ImageProps;
    topEyebrow?: EyebrowProps;
    topEyebrowIsBig?: boolean;
    variant?: DividedListVariantOptions;
    caption?: WithCaptionProps;
    disableAnim?: boolean;
}

export const DividedListItem = (props: DividedListItemProps): JSX.Element => {
    const isSm = useBreakpointDown('sm');
    const mdUp = useBreakpointUp('md');
    const {
        accentColor,
        bottomEyebrow,
        callToAction,
        className,
        description,
        headline,
        title,
        href,
        image = {
            alt: '',
            ratio: 'rect',
            src: ''
        },
        topEyebrow,
        topEyebrowIsBig = true,
        variant = 'standard',
        caption,
        disableAnim
    } = props;

    const hasImage = image && image.src;
    let imageColGridFlex = {};
    let middleColGridFlex = {};

    switch (variant) {
        case 'offset-description':
            imageColGridFlex = {
                xs: 12,
                md: 4
            };
            middleColGridFlex = {
                xs: 12,
                md: hasImage ? 3 : 7
            };
            break;
        default:
            imageColGridFlex = {
                xs: 12,
                md: 5
            };
            middleColGridFlex = {
                xs: 12,
                md: hasImage ? 7 : 12
            };
    }

    const descriptionContent = (
        <>
            <Typography variant="p" caption={variant === 'standard' ? true : false}>
                {description}
            </Typography>
        </>
    );

    return (
        <DividedListItemWrapper className={className}>
            <AnimateOnViewWrap translateY={30} disable={disableAnim}>
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="stretch"
                    direction="row"
                    spacing={isSm ? 3 : 4}>
                    {hasImage && (
                        <Grid item {...imageColGridFlex}>
                            <UeLink href={href} title={headline || title}>
                                <WithCaption {...caption}>
                                    <Image
                                        src={image.src}
                                        ratio={image.ratio}
                                        alt={image.alt ? image.alt : 'Image of creator'}
                                    />
                                </WithCaption>
                            </UeLink>
                        </Grid>
                    )}
                    <Grid item {...middleColGridFlex}>
                        {topEyebrow && topEyebrow.text && (
                            <>
                                <Typography variant="h4" big={topEyebrowIsBig} as="span">
                                    {topEyebrow.text}
                                </Typography>
                                <VerticalSpacer variant="eyebrow" />
                            </>
                        )}
                        {(headline || title) && (
                            <TextLink href={href} accentColor={accentColor}>
                                <Typography variant="h3" big>
                                    {headline || title}
                                </Typography>
                            </TextLink>
                        )}
                        {description && variant === 'standard' && (
                            <>
                                <VerticalSpacer variant="paragraph" />
                                {descriptionContent}
                            </>
                        )}
                        {bottomEyebrow && bottomEyebrow.text && (
                            <>
                                <VerticalSpacer
                                    variant="custom"
                                    spacing={variant === 'offset-description' ? 1 : 2}
                                />
                                <Eyebrow {...bottomEyebrow} as="span" />
                            </>
                        )}
                        {href && callToAction && (
                            <>
                                <VerticalSpacer variant="custom" spacing={3} />
                                <Button type="secondary" color={accentColor} href={href}>
                                    {callToAction}
                                </Button>
                            </>
                        )}
                    </Grid>
                    {variant === 'offset-description' && (
                        <>
                            <Grid item xs={12} md={4}>
                                <Grid
                                    container
                                    className="divided-list-offset"
                                    direction="column"
                                    justifyContent="flex-end">
                                    {descriptionContent}
                                </Grid>
                            </Grid>

                            {mdUp ? <Grid item xs={false} md={1} /> : ''}
                        </>
                    )}
                </Grid>
            </AnimateOnViewWrap>
        </DividedListItemWrapper>
    );
};
